import { makeAutoObservable, runInAction } from "mobx";
import {
  adminGetContractLink,
  adminGenerateContract,
} from "@pro-sto/api-client";
import { OpenAPI } from "@pro-sto/api-client";
import { toastStore } from "../../../ToastStore";

// ─── Стор управления PDF-договором ───────────────────────────────────────────

export class ContractSubStore {
  contractLink: string | null = null;
  isLoadingLink = false;
  isGenerating = false;
  isDownloading = false;

  constructor() {
    makeAutoObservable(this);
  }

  /** Загрузка ссылки на существующий договор. 404 — договора ещё нет, не ошибка. */
  loadContractLink = async (uuid: string) => {
    if (!uuid) return;
    this.isLoadingLink = true;
    try {
      const res = await adminGetContractLink({ serviceCenterUuid: uuid });
      runInAction(() => {
        const url =
          (res as { url?: string })?.url ??
          (typeof res === "string" ? res : null);
        this.contractLink = url ?? null;
        this.isLoadingLink = false;
      });
    } catch (e: unknown) {
      runInAction(() => {
        const status = (e as { status?: number })?.status;
        if (status !== 404) {
          toastStore.showError("Не удалось загрузить ссылку на договор");
        }
        this.contractLink = null;
        this.isLoadingLink = false;
      });
    }
  };

  /** Генерация договора, затем автоматическая загрузка ссылки. */
  generateContract = async (uuid: string) => {
    if (!uuid) return;
    this.isGenerating = true;
    try {
      await adminGenerateContract({ serviceCenterUuid: uuid });
      await this.loadContractLink(uuid);
    } catch (e: unknown) {
      const status = (e as { status?: number })?.status;
      if (status === 400) {
        toastStore.showError("Недостаточно данных для генерации договора");
      } else {
        toastStore.showError("Не удалось сформировать договор");
      }
    } finally {
      runInAction(() => {
        this.isGenerating = false;
      });
    }
  };

  /**
   * Скачивает PDF через fetch с Authorization-заголовком и открывает в новом окне.
   * Нужно, т.к. auth реализован через Bearer-заголовок — прямое открытие ссылки не работает.
   */
  downloadAndOpen = async (uuid: string) => {
    if (!uuid || this.isDownloading) return;
    runInAction(() => { this.isDownloading = true; });

    const token =
      typeof OpenAPI.TOKEN === "string"
        ? OpenAPI.TOKEN
        : (localStorage.getItem("accessToken") ?? "");

    const base = OpenAPI.BASE || "";
    const url = `${base}/api/admin/service-centers/${uuid}/contractor-data/contract`;

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, "_blank");

      // Освобождаем blob URL после того, как браузер успел его открыть
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

      if (!win) {
        toastStore.showError("Браузер заблокировал открытие окна. Разрешите всплывающие окна.");
      }
    } catch {
      toastStore.showError("Не удалось открыть договор");
    } finally {
      runInAction(() => { this.isDownloading = false; });
    }
  };

  reset = () => {
    this.contractLink = null;
    this.isLoadingLink = false;
    this.isGenerating = false;
    this.isDownloading = false;
  };
}
